import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, makeStyles, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import StyleButton from '../../../../components/StyleButton';
import { CheckIcon, DocIcon, TickIcon } from '../../../../components/Icon';
import Text from '../../../../components/Text';
import TableUI from '../../../../components/TableUI';
import { ORDER_SERVICE_STATUS_ENUM } from '../../../../comm/constant';
import { bedStatusAPI } from '../../../../apis';
import AutoCompleteMultiCheckbox from '../../../../components/AutoCompleteMultiCheckbox';
import { downloadFileById, useGetUserInfo } from '../../../../utils';
import exportPwdExcel from '../../../../utils/exportPwdExcel';
import CustomDateTimePicker from '../../../../components/DatePicker/CustomDateTimePicker';
import { i18n } from '../../../../language';
import { checkSubmitData } from '../../../../utils/check';
import { BedStatusStatisticContext } from '../..';
import { DeleteServiceOrderBtn } from '../../components/ServiceOrder';
import useCheckLogin from '../../../../utils/useCheckLogin';

const pageSize = 20;
const ServiceHistory = () => {
  const classes = useStyles();
  const [filter, setFilter] = useState({
    startDate: moment().add(-2, 'd'),
    endDate: moment(),
    status: [],
  });
  const [historyList, setHistoryList] = useState({
    count: 0,
    data: [],
  });
  const userInfo = useGetUserInfo();
  const [errorMsg, setErrorMsg] = useState(null);
  const language = useSelector((state) => state.settings.language);
  const { refresh } = useContext(BedStatusStatisticContext);
  const checkLogin = useCheckLogin();

  const updateFilter = (type) => (value) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [type]: value,
    }));
  };

  const statusOptions = _.map(ORDER_SERVICE_STATUS_ENUM, (item, key) => ({
    value: key,
    text: item.text,
  }));

  const params = {
    endDate: moment(filter.endDate).format('YYYY-MM-DD 23:59:59'),
    startDate: moment(filter.startDate).format('YYYY-MM-DD 00:00:00'),
    status: _.map(filter.status, (item) => item.value),
  };

  // 验证日期不超过30天
  const verifyDate = moment(filter.startDate).isBetween(
    moment(filter.endDate).add('-29', 'days'),
    moment(filter.endDate),
    'days',
    '[]'
  );

  const getCleansingHistory = async (pageNo) => {
    try {
      if (verifyDate) {
        const { result } = await bedStatusAPI.getCleansingHistory({ ...params, pageNo, pageSize });
        if (pageNo > 1) {
          setHistoryList((prevData) => {
            let tempData = prevData.data;
            prevData.data.splice(pageNo * pageSize, pageSize, ...result.data);
            return {
              count: result.count,
              data: tempData,
            };
          });
        } else {
          setHistoryList(result);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCleansingHistory(1);
  }, [filter, refresh]);

  const turnPages = useCallback(() => {
    const maxCount = historyList?.count;
    const currentCount = historyList?.data?.length;
    if (maxCount > currentCount) {
      getCleansingHistory(currentCount / pageSize + 1);
    }
  }, [historyList]);

  const handleExport = async (protectKey) => {
    try {
      if (verifyDate) {
        await downloadFileById(
          () => {
            return bedStatusAPI.getServiceHistoryExportRequest({
              ...params,
              hospId: userInfo?.hospId,
              wardId: userInfo?.wardId,
              protectKey,
            });
          },
          () => {
            return `Service History Export Excel_${moment().valueOf()}`;
          }
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const schema = {
    startDate: filter.endDate
      ? Yup.date()
          .max(
            filter.endDate,
            i18n.t('ALERT_START_DATE_CANNOT_BE_LATER_THAN_END_DATE_CANNOT_BE_LATE_THAN_ENDDATE', {
              value: filter.endDate?.format('DD-MM-yyyy'),
            })
          )
          .min(
            moment(filter.endDate).subtract(30, 'days'),
            i18n.t('BEDSTATUS_START_DATE_AND_END_DATE_CANNOT_BE_OVER_N_DAYS', { value: 30 })
          )
          .typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }))
      : Yup.date().typeError(i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') })),
    stopDate: Yup.date().typeError(
      i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_END_DATE') })
    ),
  };

  useEffect(() => {
    (async () => {
      try {
        await checkSubmitData(Yup.object().shape(schema), { startDate: filter.startDate, stopDate: filter.endDate });
        setErrorMsg({ ...errorMsg, startDate: null, stopDate: null });
      } catch (e) {
        setErrorMsg({ ...errorMsg, ...e });
      }
    })();
  }, [language, filter.startDate, filter.endDate]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      p="1.87rem"
      borderRadius="15px"
      bgcolor="#ffffff"
      height="calc(-12rem + 100vh)"
    >
      <Box display="flex" justifyContent="space-between">
        <Text color="#2E4469" fontSize="1.5rem" fontFamily="NotoSans-Bold" mb="0.62rem">
          Service History
        </Text>
        <Box display="flex">
          <StyleButton
            Icon={DocIcon}
            text="Export"
            type="fill"
            width="10rem"
            height="3rem"
            onClick={() => {
              if (!checkLogin()) return;
              exportPwdExcel(handleExport)();
            }}
          />
        </Box>
      </Box>
      <Box mb="1rem">
        <Box display="flex" alignItems="center">
          <Text fontSize="1rem" color="#2E4469" mr="1.25rem">
            Date:
          </Text>
          <Box display="flex" alignItems="center" gridGap="0.5rem">
            <CustomDateTimePicker
              value={filter.startDate}
              onChange={updateFilter('startDate')}
              width="10rem"
              height="3.1rem"
              iconColor="#538fff"
              format="YYYY-MM-DD"
              datepicker
              strictCompareDates={false}
            />
            <Text>-</Text>
            <CustomDateTimePicker
              value={filter.endDate}
              onChange={updateFilter('endDate')}
              width="10rem"
              height="3.1rem"
              iconColor="#538fff"
              format="YYYY-MM-DD"
              datepicker
              strictCompareDates={false}
            />
          </Box>
          <Box display="flex" alignItems="center" gridGap="0.625rem" ml="2.5rem">
            <Text color="#2e4469" fontSize="1rem">
              Status:
            </Text>
            <AutoCompleteMultiCheckbox
              width="12.5rem"
              height="3.1rem"
              itemList={statusOptions}
              value={filter.status}
              onChange={updateFilter('status')}
            />
          </Box>
        </Box>
        {errorMsg?.startDate !==
        i18n.t('DUTYASSIGN_TEMPLATE_AUTORULE_ERROR', { value: i18n.t('EHANDOVER_START_DATE') }) ? (
          <Typography className={classes.errorMsg}>{errorMsg?.startDate ?? ''}</Typography>
        ) : (
          ''
        )}
      </Box>
      <TableUI turnPages={turnPages}>
        <TableHead>
          <TableRow>
            <TableCell>Ordered Date</TableCell>
            <TableCell>Order By</TableCell>
            <TableCell>Service type</TableCell>
            <TableCell>Service Remark</TableCell>
            <TableCell>Is Urgent</TableCell>
            <TableCell>Ward</TableCell>
            <TableCell>Cubicle</TableCell>
            <TableCell>Bed No.</TableCell>
            <TableCell>Arrival Date</TableCell>
            <TableCell>Completed Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {historyList?.data?.map((item) => {
            const status = ORDER_SERVICE_STATUS_ENUM?.[item?.orderStatus];
            return (
              <TableRow key={item.bsCleansingRequestId}>
                <TableCell>{item?.orderDate && moment(item?.orderDate).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>{item.orderBy}</TableCell>
                <TableCell>{item.serviceDesc}</TableCell>
                <TableCell>{item.serviceRemark}</TableCell>
                <TableCell>{item.urgent === 'Y' ? <CheckIcon className={classes.checkIcon} /> : ''}</TableCell>
                <TableCell>{item.wardName}</TableCell>
                <TableCell>{item.cubicleName}</TableCell>
                <TableCell>{item.bedNo}</TableCell>
                <TableCell>{item?.arrivalDate && moment(item?.arrivalDate).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>{item?.completedDate && moment(item?.completedDate).format('YYYY-MM-DD HH:mm')}</TableCell>
                <TableCell>
                  <Box display="flex" gridGap="0.62rem" alignItems="center">
                    <Box
                      bgcolor={status?.color}
                      border={item?.orderStatus === 'W' ? 'solid 1px #BBBBBB' : 'unset'}
                      width="0.625rem"
                      height="0.625rem"
                      borderRadius="50%"
                    />
                    <Text fontSize="1rem" color="#2e4469">
                      {status?.text}
                    </Text>
                  </Box>
                </TableCell>
                <TableCell>
                  {item?.orderStatus !== 'C' && item?.orderStatus !== 'Y' && (
                    <DeleteServiceOrderBtn
                      data={item}
                      reminder={`bed ${item?.bedNo}`}
                      refreshList={() => getCleansingHistory(1)}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </TableUI>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  checkIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#28d2ab',
  },
}));

export default ServiceHistory;
